import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { take } from 'rxjs';

import { LandingCheckoutService } from './landing-checkout.service';

@Component({
  selector: 'app-checkout-success',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <main class="success-page">
      <section class="success-shell">
        <header class="landing-topbar glass-card">
          <div class="brand-area">
            <div class="brand-logo"><img src="/favicon.png" alt="The Gherkin Forge" /></div>
            <div class="brand-copy">
              <div class="brand-title">The Gherkin Forge</div>
              <div class="brand-subtitle">Subscription confirmed</div>
            </div>
          </div>

          <div class="hero-actions">
            <a class="secondary-button login-topbar-button" routerLink="/">Back to landing</a>
            <a class="primary-button" routerLink="/login">Sign in</a>
          </div>
        </header>

        <section class="success-card glass-card">
          <div class="eyebrow">Stripe checkout</div>
          <h1 class="hero-title success-title">
            {{ title() }}
          </h1>
          <p class="section-copy success-copy">
            {{ description() }}
          </p>

          <div class="success-grid">
            <article class="success-detail-card">
              <span class="login-trust-label">Next step</span>
              <strong>{{ internalTeamStatus() }}</strong>
            </article>
            <article class="success-detail-card">
              <span class="login-trust-label">Customer email</span>
              <strong>{{ customerEmailStatus() }}</strong>
            </article>
          </div>

          <div class="hero-cta-row">
            <a class="primary-button" routerLink="/pricing">Review pricing</a>
            <a class="secondary-button" routerLink="/contact">Talk to sales</a>
          </div>
        </section>
      </section>
    </main>
  `
})
export class CheckoutSuccessComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly checkoutService = inject(LandingCheckoutService);

  readonly title = signal('Finalizing your subscription...');
  readonly description = signal('We are confirming your checkout with the backend and triggering the onboarding flow.');
  readonly internalTeamStatus = signal('Waiting for backend confirmation.');
  readonly customerEmailStatus = signal('Waiting for backend confirmation.');

  constructor() {
    const sessionId = String(this.route.snapshot.queryParamMap.get('session_id') || '').trim();

    if (!sessionId) {
      this.title.set('Missing checkout session.');
      this.description.set('The success page is missing the Stripe session id, so backend confirmation could not start.');
      this.internalTeamStatus.set('Confirmation not started.');
      this.customerEmailStatus.set('Confirmation not started.');
      return;
    }

    this.checkoutService.confirmCheckoutSession(sessionId)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          if (!result.confirmed) {
            this.title.set('Payment received, backend confirmation pending.');
            this.description.set('Stripe returned the session, but the payment is not fully confirmed by the backend yet. Please refresh in a moment.');
            this.internalTeamStatus.set('Waiting for confirmed checkout status.');
            this.customerEmailStatus.set('Confirmation email will be sent once backend confirmation completes.');
            return;
          }

          this.title.set('Your subscription is confirmed.');
          this.description.set(
            result.alreadyProcessed
              ? 'Your payment was already confirmed by the backend and the onboarding flow is in motion.'
              : 'Your payment was confirmed by the backend and the onboarding flow is now in motion.'
          );
          this.internalTeamStatus.set(
            result.alreadyProcessed
              ? 'Internal team had already received the subscription details.'
              : 'Internal team has just received the subscription details.'
          );
          this.customerEmailStatus.set(
            result.alreadyProcessed
              ? 'Confirmation email had already been triggered.'
              : 'Confirmation email has just been triggered.'
          );
        },
        error: () => {
          this.title.set('Payment received, confirmation retry needed.');
          this.description.set('Stripe checkout succeeded, but backend confirmation could not be completed from this page yet.');
          this.internalTeamStatus.set('Confirmation retry required.');
          this.customerEmailStatus.set('Confirmation retry required.');
        }
      });
  }
}
